<template>
  <div class="h-100">
    <toastification-loading-content ref="loadingToast" />
    <div class="dmp-goback px-2 pt-2" v-if="source === 'default'" @click="goBack()">
      <button class="btn btn-sm rounded-circle p-25" :class="isDark ? 'btn-danger' : 'btn-primary'">
        <i class="feather icon-arrow-left arrow-icon"></i>
      </button>
      <span class="dmp-goback__text text-capitalize">{{ $t("go_back") }}</span>
    </div>

    <b-card :class="{
      'm-2 p-2': source === 'default',
      'm-0 shadow-none showScroll': source !== 'default',
    }" no-body>
      <!-- Encabezado: identidad del panel + búsqueda + sincronizar -->
      <div class="dmp-header" :class="{ 'dmp-header--compact': source !== 'default' }">
        <div class="dmp-header__identity" v-if="source === 'default'">
          <div class="dmp-header__avatar">
            <i class="feather icon-hard-drive"></i>
          </div>
          <div class="dmp-header__text">
            <h4 v-if="panel.id" class="dmp-header__name">{{ panel.name }}</h4>
            <span v-if="panel.devices && panel.devices.panel" class="dmp-header__status"
              :class="getStatusClass(panel.devices.panel.status, 'panel')">
              {{ getPanelStatusText(panel.devices.panel.status) }}
            </span>
          </div>
        </div>

        <div class="dmp-header__tools">
          <div class="dmp-search">
            <i class="feather icon-search dmp-search__icon"></i>
            <b-form-input v-model="textFilter" :placeholder="$t('table.search')" class="dmp-search__input"
              :class="{ 'form-control-sm': source !== 'default' }" />
          </div>
          <b-button variant="primary" class="dmp-header__sync" @click="syncDevices()" :disabled="loading"
            :class="{ 'btn-sm': source !== 'default' }" v-if="$can('sync_devices', 'DmpPanel')">
            <i class="feather icon-refresh-cw mr-50" :class="{ 'dmp-spin': loading }"></i>
            <span class="text-nowrap text-capitalize">{{ $t("dmp.syncDevices") }}</span>
          </b-button>
        </div>
      </div>

      <!-- PANEL principal: dispositivo principal destacado tipo "hero" -->
      <div v-if="panel.devices && panel.devices.panel" class="panel-hero"
        :class="{ 'panel-hero--compact': source !== 'default' }">

        <!-- Cabecera del hero: identidad + estado -->
        <div class="panel-hero__head">
          <div class="panel-hero__identity">
            <div class="panel-hero__icon">
              <i class="feather icon-cpu"></i>
            </div>
            <div class="panel-hero__titles">
              <span class="panel-hero__name">
                {{ panel.devices.panel.text || panel.name || trans.panel }}
              </span>
              <div class="panel-hero__meta">
                <span class="panel-hero__chip">
                  <i class="feather icon-hash"></i>{{ panel.devices.panel.id }}
                </span>
                <span class="panel-hero__chip">
                  <i class="feather icon-tag"></i>{{ panel.devices.panel.device_type.toUpperCase() }}
                </span>
              </div>
            </div>
          </div>
          <span class="panel-hero__state" :class="getStatusClass(panel.devices.panel.status, 'panel')">
            <span class="panel-hero__state-dot"></span>
            {{ getPanelStatusText(panel.devices.panel.status) }}
          </span>
        </div>

        <!-- Cuerpo: estado del sistema + acciones -->
        <div class="panel-hero__body">
          <div class="panel-hero__section">

            <span class="panel-hero__section-title">{{ $t('dmp.dateAndTimezone') }}</span>
            <div class="panel-stat mb-2">
              <i class="feather icon-clock"></i>
              <div class="panel-stat__text">
                <span class="panel-stat__label">{{ $t('dmp.current') }}</span>
                <span class="panel-stat__value"> {{ formatDateTime(liveTime) }} | {{
                  getZoneFromOffset(panel.device.camera.summary.timezone.offset) }}</span>
              </div>
            </div>


            <span class="panel-hero__section-title">{{ $t('dmp.status') }}</span>
            <div class="panel-stats">
              <div class="panel-stat" v-if="panelStatuses.tamper !== 'NOT_USED'">
                <i class="feather icon-shield"></i>
                <div class="panel-stat__text">
                  <span class="panel-stat__label">{{ $t('dmp.panelInfo.tamper') }}</span>
                  <span class="panel-stat__value">{{ getDeviceStatusText(panelStatuses.tamper) }}</span>
                </div>
              </div>
              <div class="panel-stat" v-if="panelStatuses.acPower !== 'NOT_USED'">
                <i class="feather icon-zap"></i>
                <div class="panel-stat__text">
                  <span class="panel-stat__label">{{ $t('dmp.panelInfo.acPower') }}</span>
                  <span class="panel-stat__value">{{ getDeviceStatusText(panelStatuses.acPower) }}</span>
                </div>
              </div>
              <div class="panel-stat" v-if="panelStatuses.battery !== 'NOT_USED'">
                <i class="feather icon-battery-charging"></i>
                <div class="panel-stat__text">
                  <span class="panel-stat__label">{{ $t('dmp.panelInfo.battery') }}</span>
                  <span class="panel-stat__value">{{ getDeviceStatusText(panelStatuses.battery) }}</span>
                </div>
              </div>
              <div class="panel-stat" v-if="panelStatuses.printer !== 'NOT_USED'">
                <i class="feather icon-printer"></i>
                <div class="panel-stat__text">
                  <span class="panel-stat__label">{{ $t('dmp.panelInfo.printer') }}</span>
                  <span class="panel-stat__value">{{ getDeviceStatusText(panelStatuses.printer) }}</span>
                </div>
              </div>
              <div class="panel-stat" v-if="panelStatuses.wireless !== 'NOT_USED'">
                <i class="feather icon-wifi"></i>
                <div class="panel-stat__text">
                  <span class="panel-stat__label">{{ $t('dmp.panelInfo.wireless') }}</span>
                  <span class="panel-stat__value">{{ getDeviceStatusText(panelStatuses.wireless) }}</span>
                </div>
              </div>
              <div class="panel-stat" v-if="panelStatuses.phoneLine1 !== 'NOT_USED'">
                <i class="feather icon-phone"></i>
                <div class="panel-stat__text">
                  <span class="panel-stat__label">{{ $t('dmp.panelInfo.phoneLine1') }}</span>
                  <span class="panel-stat__value">{{ getDeviceStatusText(panelStatuses.phoneLine1) }}</span>
                </div>
              </div>
              <div class="panel-stat" v-if="panelStatuses.phoneLine2 !== 'NOT_USED'">
                <i class="feather icon-phone"></i>
                <div class="panel-stat__text">
                  <span class="panel-stat__label">{{ $t('dmp.panelInfo.phoneLine2') }}</span>
                  <span class="panel-stat__value">{{ getDeviceStatusText(panelStatuses.phoneLine2) }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="panel-hero__section panel-hero__section--actions">
            <span class="panel-hero__section-title">{{ $t('dmp.actions') }}</span>
            <dmp-panel class="panel-hero__actions" :device="panel.devices.panel" :trans="trans"
              :loadingToast="loadingToast" :updateStatus="updateStatus" />
          </div>
        </div>
      </div>

      <!-- Tabs: cada tipo de device en su propia pestaña -->
      <b-tabs v-model="activeTabIndex" pills class="dmp-device-tabs"
        :nav-wrapper-class="source === 'default' ? 'px-1 pt-1' : 'px-0 pt-50'">

        <!-- Tab: Resultados de la búsqueda (solo visible al buscar globalmente) -->
        <b-tab v-if="hasGlobalSearch" title-link-class="text-primary">
          <template #title>
            <feather-icon icon="SearchIcon" size="16" class="mr-50" />
            <span class="text-capitalize">{{ $t('dmp.searchResults') }} ({{ searchResultsCount }})</span>
          </template>

          <div :class="{ 'container-fluid': source === 'default', 'devices-table-wrapper--grid': source !== 'default' }"
            class="devices-table-wrapper">

            <b-row class="text-muted p-1 mx-0 table-fields-devices flex-nowrap device-table-grid">
              <b-col class="table-title">{{ $t('dmp.actions') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.status') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.deviceId') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.device') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.type') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.name') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.tags') }}</b-col>
            </b-row>

            <div v-if="searchResults.length" class="zones-container border-left">
              <div v-for="(result, idx) in searchResults" :key="`result-${result.kind}-${result.device.id}-${idx}`"
                class="device-row zone-row border-bottom">
                <b-row class="align-items-center p-2 flex-nowrap device-table-grid">
                  <b-col>
                    <dmp-panel v-if="result.kind === 'panel'" :device="result.device" :trans="trans"
                      :loadingToast="loadingToast" :updateStatus="updateStatus" />
                    <dmp-area v-else-if="result.kind === 'area'" :device="result.device" :trans="trans"
                      :loadingToast="loadingToast" :updateStatus="updateStatus" />
                    <dmp-door v-else-if="result.kind === 'door'" :device="result.device" :trans="trans"
                      :loadingToast="loadingToast" :updateStatus="updateStatus" />
                    <dmp-output v-else-if="result.kind === 'output'" :device="result.device" :trans="trans"
                      :loadingToast="loadingToast" :updateStatus="updateStatus" />
                    <template v-else>
                      <dmp-zone :device="result.device" :trans="trans" :loadingToast="loadingToast"
                        :updateStatus="updateStatus" />
                      <label class="form-check-label my-1 mb-0">{{ $t('dmp.virtual_bypass') }}</label>
                      <b-form-checkbox class="mt-0" v-model="result.device.virtual_bypass" switch
                        @change="handleVirtualBypass(result.device, panel.device.id, $event)">
                      </b-form-checkbox>
                    </template>
                  </b-col>
                  <b-col>
                    <span
                      :class="getStatusClass(result.device.status, result.kind === 'panel' ? 'panel' : (result.kind === 'door' ? 'door' : null))">
                      {{ result.kind === 'panel' ? getPanelStatusText(result.device.status) :
                        getDeviceStatusText(result.device.status) }}
                    </span>
                  </b-col>
                  <b-col>{{ result.device.id }}</b-col>
                  <b-col>{{ (result.device.device_type || '').toUpperCase() }}</b-col>
                  <b-col>
                    <template v-if="result.kind === 'door'">{{ result.device.door_type }}</template>
                    <template
                      v-else-if="result.kind === 'zone' || result.kind === 'zone24h' || result.kind === 'output'">
                      {{ getZoneTypeText(result.device.zone_type) }}
                    </template>
                  </b-col>
                  <b-col>{{ result.device.text }}</b-col>
                  <b-col @click.stop>
                    <v-select v-if="result.kind !== 'panel'"
                      :value="getDeviceTag(result.device.device_type, result.device.id)" :options="tagOptions"
                      :reduce="tag => tag.value" :clearable="true" @input="onResultTagChange(result, $event)"
                      :calculate-position="withPopper" />
                  </b-col>
                </b-row>
              </div>
            </div>

            <div v-else class="text-center text-muted p-3">
              {{ $t('dmp.noResults') }}
            </div>
          </div>
        </b-tab>

        <!-- Tab: Áreas (con sus zonas en acordeón) -->
        <b-tab :disabled="!areasCount">
          <template #title>
            <feather-icon icon="MapPinIcon" size="16" class="mr-50" />
            <span class="text-capitalize">{{ trans.area }} ({{ areasCount }})</span>
          </template>

          <div class="tab-search px-1 py-1">
            <b-form-input v-model="areasSearch" size="sm" :placeholder="$t('table.search')" class="tab-search-input" />
          </div>

          <div :class="{ 'container-fluid': source === 'default', 'devices-table-wrapper--grid': source !== 'default' }"
            class="devices-table-wrapper">

            <b-row class="text-muted p-1 mx-0 table-fields-devices flex-nowrap device-table-grid">
              <b-col class="table-title">{{ $t('dmp.actions') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.status') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.deviceId') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.device') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.type') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.name') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.tags') }}</b-col>
            </b-row>

            <!-- Area accordion -->
            <div v-if="panel.devices && panel.devices.areas" class="">

              <!-- Áreas CON zonas: flecha de acordeón y zonas colapsables -->
              <div v-for="area in areasWithZones" :key="`area-${area.id}`" class="area-accordion">
                <!-- Area header (clickable) -->
                <div class="area-header device-row cursor-pointer" @click="toggleArea(area.id)"
                  :class="{ 'area-expanded': expandedAreas.includes(area.id) }">
                  <b-row class="align-items-center p-2 flex-nowrap device-table-grid">
                    <b-col class="pr-0">
                      <div class="d-flex align-items-center">
                        <i class="feather mr-1 transition-all" :class="expandedAreas.includes(area.id)
                          ? 'icon-chevron-down'
                          : 'icon-chevron-right'
                          "></i>
                        <div v-if="!area.text.includes('*UNUSED*')">
                          <dmp-area :device="area" :trans="trans" :loadingToast="loadingToast"
                            :updateStatus="updateStatus" />
                        </div>

                      </div>
                    </b-col>
                    <b-col>
                      <div v-if="!area.text.includes('*UNUSED*')">
                        <span :class="getStatusClass(area.status)">
                          {{ getDeviceStatusText(area.status) }}
                        </span>
                      </div>
                    </b-col>
                    <b-col>{{ area.id }}</b-col>
                    <b-col>{{ area.device_type.toUpperCase() }}</b-col>
                    <b-col></b-col>
                    <b-col>{{ area.text }}</b-col>
                    <div v-if="!area.text.includes('*UNUSED*')" class="">
                      <b-col @click.stop>
                        <v-select :value="getDeviceTag(area.device_type, area.id)" :options="tagOptions"
                          :reduce="tag => tag.value" :clearable="true" @input="onAreaDeviceTagChange(area, $event)"
                          :calculate-position="withPopper" />
                      </b-col>
                    </div>
                  </b-row>
                </div>
                <!-- Collapsible content with the areas zones -->
                <b-collapse :visible="expandedAreas.includes(area.id)" class="area-content">
                  <div class="zones-container border-left">
                    <div v-for="zone in area.devices" :key="`zone-${zone.id}`"
                      class="device-row zone-row border-bottom">
                      <b-row class="align-items-center p-2 flex-nowrap device-table-grid">
                        <b-col>
                          <dmp-zone :device="zone" :trans="trans" :loadingToast="loadingToast"
                            :updateStatus="updateStatus" />
                          <label class="form-check-label my-1 mb-0" for="checkDefault">
                            {{ $t('dmp.virtual_bypass') }}
                          </label>
                          <b-form-checkbox class="mt-0" v-model="zone.virtual_bypass" switch
                            @change="handleVirtualBypass(zone, panel.device.id, $event)">
                          </b-form-checkbox>
                        </b-col>
                        <b-col>
                          <span :class="getStatusClass(zone.status)">
                            {{ getDeviceStatusText(zone.status) }}
                          </span>
                        </b-col>
                        <b-col>{{ zone.id }}</b-col>
                        <b-col>{{ zone.device_type.toUpperCase() }}</b-col>
                        <b-col>{{ getZoneTypeText(zone.zone_type) }}</b-col>
                        <b-col>{{ zone.text }}</b-col>
                        <b-col>
                          <v-select :value="getDeviceTag(zone.device_type, zone.id)" :options="tagOptions"
                            :reduce="tag => tag.value" :clearable="true"
                            @input="onChildDeviceTagChange(zone.device_type, zone.id, $event)"
                            :calculate-position="withPopper" />
                        </b-col>
                      </b-row>
                    </div>
                  </div>
                </b-collapse>
              </div>

              <!-- Separador entre áreas sin zonas y áreas con zonas -->
              <div v-if="areasWithoutZones.length && areasWithZones.length" class="areas-group-divider"></div>

              <!-- Áreas SIN zonas: sin flecha de acordeón y sin contenido colapsable -->
              <div v-for="area in areasWithoutZones" :key="`area-nozone-${area.id}`" class="area-accordion">
                <div class="area-header area-header--no-zones device-row">
                  <b-row class="align-items-center p-2 flex-nowrap device-table-grid">
                    <b-col class="pr-0">
                      <div class="d-flex align-items-center">
                        <!-- Placeholder invisible: oculta la flecha pero conserva la alineación de columnas -->
                        <i class="feather mr-1 transition-all icon-chevron-right invisible"></i>
                        <div v-if="!area.text.includes('*UNUSED*')">
                          <dmp-area :device="area" :trans="trans" :loadingToast="loadingToast"
                            :updateStatus="updateStatus" />
                        </div>
                      </div>
                    </b-col>
                    <b-col>
                      <div v-if="!area.text.includes('*UNUSED*')">
                        <span :class="getStatusClass(area.status)">
                          {{ getDeviceStatusText(area.status) }}
                        </span>
                      </div>
                    </b-col>
                    <b-col>{{ area.id }}</b-col>
                    <b-col>{{ area.device_type.toUpperCase() }}</b-col>
                    <b-col></b-col>
                    <b-col>{{ area.text }}</b-col>
                    <div v-if="!area.text.includes('*UNUSED*')" class="">
                      <b-col @click.stop>
                        <v-select :value="getDeviceTag(area.device_type, area.id)" :options="tagOptions"
                          :reduce="tag => tag.value" :clearable="true" @input="onAreaDeviceTagChange(area, $event)"
                          :calculate-position="withPopper" />
                      </b-col>
                    </div>
                  </b-row>
                </div>
              </div>

            </div>
          </div>
        </b-tab>

        <!-- Tab: Zonas 24h -->
        <b-tab :disabled="!zones24hCount">
          <template #title>
            <feather-icon icon="ClockIcon" size="16" class="mr-50" />
            <span class="text-capitalize">{{ trans.zone24h }} ({{ zones24hCount }})</span>
          </template>

          <div class="tab-search px-1 py-1">
            <b-form-input v-model="zones24hSearch" size="sm" :placeholder="$t('table.search')"
              class="tab-search-input" />
          </div>

          <div v-if="panel.devices && panel.devices.zones24h && panel.devices.zones24h.length > 0"
            :class="{ 'container-fluid': source === 'default', 'devices-table-wrapper--grid': source !== 'default' }"
            class="devices-table-wrapper">

            <b-row class="text-muted p-1 mx-0 table-fields-devices flex-nowrap device-table-grid">
              <b-col class="table-title">{{ $t('dmp.actions') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.status') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.deviceId') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.device') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.type') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.name') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.tags') }}</b-col>
            </b-row>

            <!-- Etiqueta para todo el grupo -->
            <div class="device-row group-tag-bar">
              <b-row class="align-items-center p-1 mx-0 flex-nowrap device-table-grid">
                <b-col></b-col>
                <b-col></b-col>
                <b-col></b-col>
                <b-col></b-col>
                <b-col></b-col>
                <b-col class="text-right pr-1">
                  <small class="text-muted text-uppercase font-weight-bold">{{ $t('dmp.tags') }}</small>
                </b-col>
                <b-col @click.stop>
                  <v-select :value="getDeviceTag('zones24h', 'group')" :options="tagOptions" :reduce="tag => tag.value"
                    :clearable="true" @input="onGroupDeviceTagChange('zones24h', filteredZones24h, $event)"
                    :calculate-position="withPopper" />
                </b-col>
              </b-row>
            </div>

            <div class="zones-container border-left">
              <div v-for="zone24h in filteredZones24h" :key="`zone24h-${zone24h.id}`"
                class="device-row zone-row border-bottom">
                <b-row class="align-items-center p-2 flex-nowrap device-table-grid">
                  <b-col>
                    <dmp-zone :device="zone24h" :trans="trans" :loadingToast="loadingToast"
                      :updateStatus="updateStatus" />
                    <label class="form-check-label my-1 mb-0" for="checkDefault">
                      {{ $t('dmp.virtual_bypass') }}
                    </label>
                    <b-form-checkbox class="mt-0" v-model="zone24h.virtual_bypass" switch
                      @change="handleVirtualBypass(zone24h, panel.device.id, $event)">
                    </b-form-checkbox>
                  </b-col>
                  <b-col>
                    <span :class="getStatusClass(zone24h.status)">
                      {{ getDeviceStatusText(zone24h.status) }}
                    </span>
                  </b-col>
                  <b-col>{{ zone24h.id }}</b-col>
                  <b-col>{{ zone24h.device_type.toUpperCase() }}</b-col>
                  <b-col>{{ getZoneTypeText(zone24h.zone_type) }}</b-col>
                  <b-col>{{ zone24h.text }}</b-col>
                  <b-col>
                    <v-select :value="getDeviceTag(zone24h.device_type, zone24h.id)" :options="tagOptions"
                      :reduce="tag => tag.value" :clearable="true"
                      @input="onChildDeviceTagChange(zone24h.device_type, zone24h.id, $event)"
                      :calculate-position="withPopper" />
                  </b-col>
                </b-row>
              </div>
            </div>
          </div>
        </b-tab>

        <!-- Tab: Salidas -->
        <b-tab :disabled="!outputsCount">
          <template #title>
            <feather-icon icon="ZapIcon" size="16" class="mr-50" />
            <span class="text-capitalize">{{ trans.output }} ({{ outputsCount }})</span>
          </template>

          <div class="tab-search px-1 py-1">
            <b-form-input v-model="outputsSearch" size="sm" :placeholder="$t('table.search')"
              class="tab-search-input" />
          </div>

          <div v-if="panel.devices && panel.devices.outputs && panel.devices.outputs.length > 0"
            :class="{ 'container-fluid': source === 'default', 'devices-table-wrapper--grid': source !== 'default' }"
            class="devices-table-wrapper">

            <b-row class="text-muted p-1 mx-0 table-fields-devices flex-nowrap device-table-grid">
              <b-col class="table-title">{{ $t('dmp.actions') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.status') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.deviceId') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.device') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.type') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.name') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.tags') }}</b-col>
            </b-row>

            <!-- Etiqueta para todo el grupo -->
            <div class="device-row group-tag-bar">
              <b-row class="align-items-center p-1 mx-0 flex-nowrap device-table-grid">
                <b-col></b-col>
                <b-col></b-col>
                <b-col></b-col>
                <b-col></b-col>
                <b-col></b-col>
                <b-col class="text-right pr-1">
                  <small class="text-muted text-uppercase font-weight-bold">{{ $t('dmp.tags') }}</small>
                </b-col>
                <b-col @click.stop>
                  <v-select :value="getDeviceTag('outputs', 'group')" :options="tagOptions" :reduce="tag => tag.value"
                    :clearable="true" @input="onGroupDeviceTagChange('outputs', filteredOutputs, $event)"
                    :calculate-position="withPopper" />
                </b-col>
              </b-row>
            </div>

            <div class="zones-container border-left">
              <div v-for="output in filteredOutputs" :key="`output-${output.id}`"
                class="device-row zone-row border-bottom">
                <b-row class="align-items-center p-2 flex-nowrap device-table-grid">
                  <b-col>
                    <dmp-output :device="output" :trans="trans" :loadingToast="loadingToast"
                      :updateStatus="updateStatus" />
                  </b-col>
                  <b-col>
                    <span :class="getStatusClass(output.status)">
                      {{ getDeviceStatusText(output.status) }}
                    </span>
                  </b-col>
                  <b-col>{{ output.id }}</b-col>
                  <b-col>{{ output.device_type.toUpperCase() }}</b-col>
                  <b-col>{{ getZoneTypeText(output.zone_type) }}</b-col>
                  <b-col>{{ output.text }}</b-col>
                  <b-col>
                    <v-select :value="getDeviceTag(output.device_type, output.id)" :options="tagOptions"
                      :reduce="tag => tag.value" :clearable="true"
                      @input="onChildDeviceTagChange(output.device_type, output.id, $event)"
                      :calculate-position="withPopper" />
                  </b-col>
                </b-row>
              </div>
            </div>
          </div>
        </b-tab>

        <!-- Tab: Puertas -->
        <b-tab :disabled="!doorsCount">
          <template #title>
            <feather-icon icon="KeyIcon" size="16" class="mr-50" />
            <span class="text-capitalize">{{ trans.door }} ({{ doorsCount }})</span>
          </template>

          <div class="tab-search px-1 py-1">
            <b-form-input v-model="doorsSearch" size="sm" :placeholder="$t('table.search')" class="tab-search-input" />
          </div>

          <div v-if="panel.devices && panel.devices.doors && panel.devices.doors.length > 0"
            :class="{ 'container-fluid': source === 'default', 'devices-table-wrapper--grid': source !== 'default' }"
            class="devices-table-wrapper">

            <b-row class="text-muted p-1 mx-0 table-fields-devices flex-nowrap device-table-grid">
              <b-col class="table-title">{{ $t('dmp.actions') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.status') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.deviceId') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.device') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.type') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.name') }}</b-col>
              <b-col class="table-title">{{ $t('dmp.tags') }}</b-col>
            </b-row>

            <!-- Etiqueta para todo el grupo -->
            <div class="device-row group-tag-bar">
              <b-row class="align-items-center p-1 mx-0 flex-nowrap device-table-grid">
                <b-col></b-col>
                <b-col></b-col>
                <b-col></b-col>
                <b-col></b-col>
                <b-col></b-col>
                <b-col class="text-right pr-1">
                  <small class="text-muted text-uppercase font-weight-bold">{{ $t('dmp.tags') }}</small>
                </b-col>
                <b-col @click.stop>
                  <v-select :value="getDeviceTag('doors', 'group')" :options="tagOptions" :reduce="tag => tag.value"
                    :clearable="true" @input="onGroupDeviceTagChange('doors', filteredDoors, $event)"
                    :calculate-position="withPopper" />
                </b-col>
              </b-row>
            </div>

            <div class="zones-container border-left">
              <div v-for="door in filteredDoors" :key="`door-${door.id}`" class="device-row zone-row border-bottom">
                <b-row class="align-items-center p-2 flex-nowrap device-table-grid">
                  <b-col>
                    <dmp-door :device="door" :trans="trans" :loadingToast="loadingToast" :updateStatus="updateStatus" />
                  </b-col>
                  <b-col>
                    <span :class="getStatusClass(door.status, 'door')">
                      {{ getDeviceStatusText(door.status) }}
                    </span>
                  </b-col>
                  <b-col>{{ door.id }}</b-col>
                  <b-col>{{ door.device_type.toUpperCase() }}</b-col>
                  <b-col>{{ door.door_type }}</b-col>
                  <b-col>{{ door.text }}</b-col>
                  <b-col>
                    <v-select :value="getDeviceTag(door.device_type, door.id)" :options="tagOptions"
                      :reduce="tag => tag.value" :clearable="true"
                      @input="onChildDeviceTagChange(door.device_type, door.id, $event)"
                      :calculate-position="withPopper" />
                  </b-col>
                </b-row>
              </div>
            </div>
          </div>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import { BRow, BCol, BFormInput, BTable, BCollapse, BButton, BTabs, BTab } from "bootstrap-vue";
import {
  computed,
  ref,
  watch,
  onMounted,
  getCurrentInstance,
  onBeforeUnmount,
  onUnmounted
} from "@vue/composition-api";
import Swal from "sweetalert2";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { useToast } from "vue-toastification/composition";
import ToastificationLoadingContent
  from "@core/components/toastification/ToastificationLoadingContent.vue";
import ToastificationContent
  from "@core/components/toastification/ToastificationContent.vue";
import router from "@/router";
import store from "@/store";
import DmpPanel from "./devices/DmpPanel.vue";
import DmpArea from "./devices/DmpArea.vue";
import DmpZone from "./devices/DmpZone.vue";
import DmpOutput from "./devices/DmpOutput.vue";
import DmpDoor from "@/views/dmp/dmp_panels/devices/DmpDoor.vue";
import vSelect from "vue-select";
import popperHelper from "@/libs/popper-helper";
import { mapEventToStatus } from './deviceEventMap.js'
import moment from 'moment-timezone'

const ZONE_TYPE_MAP = {
  '--': 'none',
  'N': 'night',
  'DY': 'day',
  'EX': 'exit',
  'FI': 'fire',
  'SV': 'supervisory',
  'FV': 'fireVerify',
  'PN': 'panic',
  'AR': 'arming',
  'A1': 'auxiliary1',
  'A2': 'auxiliary2',
  'EM': 'emergency',
};

export default {
  name: "DMPDevices",
  components: {
    DmpDoor,
    DmpPanel,
    DmpOutput,
    DmpZone,
    ToastificationLoadingContent,
    DmpArea,
    BRow,
    BCol,
    BFormInput,
    BTable,
    BCollapse,
    BButton,
    BTabs,
    BTab,
    vSelect,
  },
  props: {
    panelId: Number | String,
    source: {
      type: String,
      default: "default",
    },
  },
  setup(props) {
    const { proxy } = getCurrentInstance();

    // COMPOSABLES
    const { t, tc } = useI18nUtils();
    const toast = useToast();

    // DATA
    const loading = ref(false);
    const panel = ref({});
    const panelStatuses = ref({});
    const bTable = ref(null);
    const textFilter = ref("");
    const areasSearch = ref("");
    const zones24hSearch = ref("");
    const outputsSearch = ref("");
    const doorsSearch = ref("");
    const activeTabIndex = ref(0);
    const loadingToast = ref(null);
    const checkStatusInterval = ref(null);
    const expandedAreas = ref([]);
    const tagOptions = computed(() =>
      store.getters['tenant/getDeviceTags'].map(tag => ({
        value: tag.id,
        label: tag.name,
      }))
    );
    const deviceTags = ref({});

    // TRANSLATIONS
    const trans = {
      "actions.cancel": t("actions.cancel"),
      "messages.activate.title": t("messages.activate.title"),
      "messages.activate.confirm": t("messages.activate.confirm"),
      "messages.desactivate.title": t("messages.desactivate.title"),
      "messages.desactivate.confirm": t("messages.desactivate.confirm"),
      "messages.loading.title": t("messages.loading.title"),
      "messages.desactivate.error": t("messages.desactivate.error"),
      "messages.activate.error": t("messages.activate.error"),
      "messages.activate.activated_correctly": t("messages.activate.activated_correctly"),
      "messages.desactivate.desactivated_correctly": t(
        "messages.desactivate.desactivated_correctly"
      ),
      "messages.desactivate.desactivated_correctly_confirm": t(
        "messages.desactivate.desactivated_correctly_confirm"
      ),
      "messages.activate.activated_correctly_confirm": t(
        "messages.activate.activated_correctly_confirm"
      ),
      "dmp.panelOffline": t("dmp.panelOffline"),
      "dmp.armPanel": t("dmp.armPanel"),
      "dmp.disarmPanel": t("dmp.disarmPanel"),
      "dmp.arming": t("dmp.arming"),
      "dmp.disarming": t("dmp.disarming"),
      "dmp.syncDevices": t("dmp.syncDevices"),
      "dmp.syncing": t("dmp.syncing"),
      "dmp.syncCorrectly": t("dmp.syncCorrectly"),
      "dmp.syncDevicesSuccess": t("dmp.syncDevicesSuccess"),
      "dmp.syncDevicesError": t("dmp.syncDevicesError"),
      "dmp.devices": t("dmp.devices"),
      "messages.activate.armed_correctly": t("messages.activate.armed_correctly"),
      "messages.activate.armed_correctly_confirm": t(
        "messages.activate.armed_correctly_confirm"
      ),
      "messages.desactivate.disarmed_correctly": t(
        "messages.desactivate.disarmed_correctly"
      ),
      "messages.desactivate.disarmed_correctly_confirm": t(
        "messages.desactivate.disarmed_correctly"
      ),
      "messages.activate.activating": t("messages.activate.activating"),
      "messages.desactivate.deactivating": t("messages.desactivate.deactivating"),
      "messages.processing": t("messages.processing"),
      "dmp.messages.executeCommandConfirm": t("dmp.messages.executeCommandConfirm"),
      "dmp.messages.commandExecutedSuccess": t("dmp.messages.commandExecutedSuccess"),
      "dmp.messages.commandExecutedError": t("dmp.messages.commandExecutedError"),
      "dmp.messages.areYouSure": t("dmp.messages.areYouSure"),
      area: t("dmp.devices.area.title"),
      arm: t("dmp.devices.area.actions.Arm"),
      disarm: t("dmp.devices.area.actions.Disarm"),
      output: t("dmp.devices.output.title"),
      Momentary: t("dmp.devices.output.actions.Momentary"),
      Off: t("dmp.devices.output.actions.Off"),
      Pulse: t("dmp.devices.output.actions.Pulse"),
      Steady: t("dmp.devices.output.actions.Steady"),
      zone: t("dmp.devices.zone.title"),
      Bypass: t("dmp.devices.zone.actions.Bypass"),
      Reset: t("dmp.devices.zone.actions.Reset"),
      'Temporal 3': t("dmp.devices.output.actions.Temporal 3"),
      zone24h: t("dmp.devices.zone24h.title"),
      panel: t("dmp.devices.panel.title"),
      'Arm Panel': t("dmp.devices.panel.actions.Arm Panel"),
      'Disarm Panel': t("dmp.devices.panel.actions.Disarm Panel"),
      'Alarm silence': t("dmp.devices.panel.actions.Alarm silence"),
      'Sensor reset': t("dmp.devices.panel.actions.Sensor reset"),
      door: t("dmp.devices.door.title"),
      'Door Access': t("dmp.devices.door.actions.Door Access"),
      'Lock Door': t("dmp.devices.door.actions.Lock Door"),
      'Unlock Door': t("dmp.devices.door.actions.Unlock Door"),
      zoneTypeNone: t("dmp.zoneInformation.zoneTypeOptions.none"),
      zoneTypeNight: t("dmp.zoneInformation.zoneTypeOptions.night"),
      zoneTypeDay: t("dmp.zoneInformation.zoneTypeOptions.day"),
      zoneTypeExit: t("dmp.zoneInformation.zoneTypeOptions.exit"),
      zoneTypeFire: t("dmp.zoneInformation.zoneTypeOptions.fire"),
      zoneTypeSupervisory: t("dmp.zoneInformation.zoneTypeOptions.supervisory"),
      zoneTypeFireVerify: t("dmp.zoneInformation.zoneTypeOptions.fireVerify"),
      zoneTypePanic: t("dmp.zoneInformation.zoneTypeOptions.panic"),
      zoneTypeArming: t("dmp.zoneInformation.zoneTypeOptions.arming"),
      zoneTypeAuxiliary1: t("dmp.zoneInformation.zoneTypeOptions.auxiliary1"),
      zoneTypeAuxiliary2: t("dmp.zoneInformation.zoneTypeOptions.auxiliary2"),
      zoneTypeEmergency: t("dmp.zoneInformation.zoneTypeOptions.emergency"),
    };

    // TABLE CONFIGURATION
    const fields = ref([
      {
        label: tc("actions.name", 2),
        key: "actions",
        thStyle: props.source !== "default" ? "width: 150px;" : "width: auto;",
      },
      {
        label: t("dmp.status"),
        key: "active",
        formatter: (value, key, item) => {
          return item.status;
        },
      },
      { label: t("dmp.type"), key: "device_type" },
      { label: t("dmp.deviceId"), key: "id" },
      { label: t("dmp.name"), key: "text" },
      {
        label: t("dmp.area"),
        key: "info",
        formatter: (value, key, item) => {
          if (item.device_type === "zone" && item.info.length > 0) {
            return `${item.info[0].name} - ${item.info[0].source_id}`;
          }
          return "N/A";
        },
      },
    ]);

    // COMPUTED PROPERTIES
    const defaultStatus = {
      tamper: "Synchronize",
      acPower: "Synchronize",
      battery: "Synchronize",
      printer: "Synchronize",
      wireless: "Synchronize",
      phoneLine1: "Synchronize",
      phoneLine2: "Synchronize",
    };

    const panelStatus = computed(() => {
      if (!panel.value.device) return "offline";

      const { device } = panel.value;

      return device.online ? (panel.value.active ? "armed" : "disarmed") : "offline";
    });

    // Mantiene panelStatuses sincronizado con el panel sin mutar dentro de una computed.
    // El acceso protegido evita el TypeError "reading 'tamper'" cuando el panel aún no
    // reporta camera/summary/status (entonces se usa defaultStatus).
    watch(
      panel,
      (value) => {
        panelStatuses.value =
          value?.device?.camera?.summary?.status || defaultStatus;
      },
      { immediate: true }
    );

    const sortById = (a, b) => Number(a.id) - Number(b.id);

    // Construye la lista de términos activos (global + el de la tab), normalizados.
    const buildTerms = (...values) =>
      values.map((v) => (v || "").trim().toLowerCase()).filter(Boolean);

    // Un device coincide si, para CADA término, alguno de sus campos lo contiene.
    const deviceMatchesTerms = (item, terms) => {
      const fields = [
        (item.id != null ? item.id.toString() : "").toLowerCase(),
        (item.text || "").toLowerCase(),
      ];
      return terms.every((term) => fields.some((f) => f.includes(term)));
    };

    // Un área coincide si ella misma o alguna de sus zonas coincide con cada término.
    const areaMatchesTerms = (area, terms) =>
      terms.every((term) => {
        const areaFields = [
          area.id.toString().toLowerCase(),
          (area.text || "").toLowerCase(),
        ];
        if (areaFields.some((f) => f.includes(term))) return true;
        return (
          area.devices &&
          area.devices.some((zone) =>
            [
              zone.id.toString().toLowerCase(),
              (zone.text || "").toLowerCase(),
            ].some((f) => f.includes(term))
          )
        );
      });

    const filterAndSortDevices = (key, tabSearchRef) => computed(() => {
      if (!panel.value.devices || !panel.value.devices[key]) return [];
      const terms = buildTerms(textFilter.value, tabSearchRef.value);
      const items =
        terms.length === 0
          ? panel.value.devices[key]
          : panel.value.devices[key].filter((item) =>
            deviceMatchesTerms(item, terms)
          );
      return [...items].sort(sortById);
    });

    const filteredAreas = computed(() => {
      if (!panel.value.devices || !panel.value.devices.areas) return [];
      const terms = buildTerms(textFilter.value, areasSearch.value);
      const areas =
        terms.length === 0
          ? panel.value.devices.areas
          : panel.value.devices.areas.filter((area) =>
            areaMatchesTerms(area, terms)
          );
      return [...areas]
        .sort(sortById)
        .map(area => ({
          ...area,
          devices: area.devices ? [...area.devices].sort(sortById) : area.devices,
        }));
    });

    // Áreas separadas según tengan o no zonas asociadas. Las que no tienen zonas
    // se muestran arriba sin flecha de acordeón; las que sí, debajo con acordeón.
    const areasWithoutZones = computed(() =>
      filteredAreas.value.filter((area) => !area.devices || area.devices.length === 0)
    );
    const areasWithZones = computed(() =>
      filteredAreas.value.filter((area) => area.devices && area.devices.length > 0)
    );

    const filteredZones24h = filterAndSortDevices('zones24h', zones24hSearch);
    const filteredOutputs = filterAndSortDevices('outputs', outputsSearch);
    const filteredDoors = filterAndSortDevices('doors', doorsSearch);

    // BÚSQUEDA GLOBAL: ¿hay término en el buscador de arriba?
    const hasGlobalSearch = computed(
      () => (textFilter.value || "").trim().length > 0
    );

    // Resultados unificados de la búsqueda global: aplana todos los tipos de
    // dispositivo que coinciden con el término global, conservando su "kind"
    // para renderizar cada fila con el componente y columnas adecuadas.
    const searchResults = computed(() => {
      const term = (textFilter.value || "").trim().toLowerCase();
      if (!term || !panel.value.devices) return [];

      const matches = (item) =>
        [
          (item.id != null ? item.id.toString() : "").toLowerCase(),
          (item.text || "").toLowerCase(),
        ].some((v) => v.includes(term));

      const devices = panel.value.devices;
      const results = [];

      if (devices.panel && matches(devices.panel)) {
        results.push({ kind: "panel", device: devices.panel });
      }

      (devices.areas || []).forEach((area) => {
        if (matches(area)) results.push({ kind: "area", device: area });
        (area.devices || []).forEach((zone) => {
          if (matches(zone))
            results.push({ kind: "zone", device: zone, areaName: area.text });
        });
      });

      (devices.zones24h || []).forEach((zone) => {
        if (matches(zone)) results.push({ kind: "zone24h", device: zone });
      });

      (devices.outputs || []).forEach((output) => {
        if (matches(output)) results.push({ kind: "output", device: output });
      });

      (devices.doors || []).forEach((door) => {
        if (matches(door)) results.push({ kind: "door", device: door });
      });

      return results;
    });

    const searchResultsCount = computed(() => searchResults.value.length);

    // Al activar/desactivar la búsqueda global, abrir/cerrar la tab de resultados.
    // La tab de resultados es la primera (índice 0) cuando existe; al limpiar la
    // búsqueda desaparece y el índice 0 pasa a ser la primera tab normal (Áreas).
    watch(hasGlobalSearch, () => {
      proxy.$nextTick(() => {
        activeTabIndex.value = 0;
      });
    });

    // Contadores por tipo: usan los datos crudos (sin textFilter) para que el
    // buscador filtre solo el contenido de la tab activa sin alterar los badges
    // de las demás tabs ni habilitar/deshabilitar pestañas según la búsqueda.
    const areasCount = computed(() =>
      panel.value.devices && panel.value.devices.areas ? panel.value.devices.areas.length : 0
    );
    const zones24hCount = computed(() =>
      panel.value.devices && panel.value.devices.zones24h ? panel.value.devices.zones24h.length : 0
    );
    const outputsCount = computed(() =>
      panel.value.devices && panel.value.devices.outputs ? panel.value.devices.outputs.length : 0
    );
    const doorsCount = computed(() =>
      panel.value.devices && panel.value.devices.doors ? panel.value.devices.doors.length : 0
    );

    const devicesFiltered = computed(() => {
      if (!textFilter.value) return panel.value.devices || [];
      return panel.value.devices.filter((device) =>
        [device.id.toString(), device.text.toLowerCase()].some((value) =>
          value.includes(textFilter.value.toLowerCase())
        )
      );
    });

    const isDark = computed(() => store.state.appConfig.layout.skin === "dark");

    const styleStatusPanel = computed(() => {
      let color = panelStatus.value === "armed" ? "green" : "orange";

      if (panelStatus.value === "offline") color = "red";

      return {
        background: color,
        width: "1rem",
        height: "1rem",
        borderRadius: "1rem",
        textAlign: "center",
        margin: "0 5px",
      };
    });

    const textStatusPanel = computed(() => {
      if (!panel.value.device) return "";

      return panel.value.device.online
        ? panel.value.active
          ? t("dmp.panelStatusArmed")
          : t("dmp.panelStatusDisarmed")
        : t("dmp.panelStatusOffline");
    });

    // METHODS

    const getZoneTypeText = (code) => {
      if (!code) return '';
      const key = ZONE_TYPE_MAP[code];
      if (!key) return code;
      return trans[`zoneType${key.charAt(0).toUpperCase() + key.slice(1)}`] || code;
    };

    const replacePlaceholders = (template, values) => {
      if (!template) return '';
      return template.replace(/%(\w+)%/g, (match, key) => {
        return values.hasOwnProperty(key) ? values[key] : match;
      });
    }

    const handleVirtualBypass = async (zone, camera_id, value) => {

      let placeholders = {
        command: proxy.$t('dmp.virtual_bypass'),
        device: zone.text
      }

      const result = await Swal.fire({
        title: proxy.$t('dmp.virtual_bypass'),
        text: `${replacePlaceholders(proxy.$t('dmp.messages.executeCommandConfirm'), placeholders)}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: proxy.$t('actions.accept'),
        cancelButtonText: proxy.$t('actions.cancel'),
        customClass: {
          confirmButton: 'btn btn-primary text-capitalize',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      });

      if (!result.isConfirmed) {
        zone.virtual_bypass = !value;
        return;
      }

      try {
        //loading.value = true;
        loadingToast.value.show(
          proxy.$t('dmp.virtual_bypass'),
          `${zone.text}`,
          'secondary'
        );

        await store.dispatch('dmp_panels/virtualBypass', {
          panel_id: camera_id,
          device_id: zone.device_id,
          action: 'virtual_bypass',
        });

        toast({
          component: ToastificationContent,
          props: {
            title: `${replacePlaceholders(proxy.$t('dmp.messages.commandExecutedSuccess'), placeholders)}`,
            icon: 'CheckIcon',
            variant: 'success',
          },
        });

        //await fetchDevices();
      } catch (error) {
        zone.virtual_bypass = !value;
        toast({
          component: ToastificationContent,
          props: {
            title: error.response?.data?.message || proxy.$t('dmp.messages.commandExecutedError'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        loading.value = false;
        loadingToast.value.close();
      }
    };


    async function loadPanel() {
      const panelId = router.currentRoute.params.id || props.panelId;
      const response = await store.dispatch("dmp_panels/show", panelId);
      applyPanel(response.data);
    }

    async function fetchDevices({ sync = true } = {}) {
      const panelId = router.currentRoute.params.id || props.panelId;
      try {
        if (sync) await store.dispatch("dmp_panels/syncPanelDevices", panelId);
      } catch (e) {
        console.error(e);
      }
      try {
        await loadPanel();
      } catch {
        panel.value = {};
      }
    }

    function initializeDeviceTags() {
      if (!panel.value || !panel.value.devices) return;

      const devices = panel.value.devices;

      // Areas and their child zones
      if (devices.areas) {
        devices.areas.forEach((area) => {
          if (area.device_tag) {
            setDeviceTag(area.device_type, area.id, area.device_tag.id);
          }
          if (area.devices) {
            area.devices.forEach((zone) => {
              if (zone.device_tag) {
                setDeviceTag(zone.device_type, zone.id, zone.device_tag.id);
              }
            });
          }
        });
      }

      // Zones 24h
      if (devices.zones24h) {
        devices.zones24h.forEach((zone24h) => {
          if (zone24h.device_tag) {
            setDeviceTag(zone24h.device_type, zone24h.id, zone24h.device_tag.id);
          }
        });
      }

      // Outputs
      if (devices.outputs) {
        devices.outputs.forEach((output) => {
          if (output.device_tag) {
            setDeviceTag(output.device_type, output.id, output.device_tag.id);
          }
        });
      }

      // Doors
      if (devices.doors) {
        devices.doors.forEach((door) => {
          if (door.device_tag) {
            setDeviceTag(door.device_type, door.id, door.device_tag.id);
          }
        });
      }
    }

    async function fetchTagOptions() {
      try {
        const tenantId = panel.value?.center?.tenant_id;
        if (!tenantId) return;
        await store.dispatch('tenant/getDeviceTagsByTenantIds', {
          'id_tenants[]': tenantId,
        });
      } catch (error) {
        console.error('Error fetching tag options:', error);
      }
    }

    async function syncDevices() {
      const result = await Swal.fire({
        title: proxy.$t("dmp.syncDevices"),
        text: proxy.$t("dmp.syncPanelWarning"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: proxy.$t("dmp.syncDevices"),
        cancelButtonText: proxy.$t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      });

      if (!result.isConfirmed) return;

      loading.value = true;
      loadingToast.value.show(
        trans["dmp.syncDevices"],
        trans["dmp.syncing"],
        "secondary"
      );

      try {
        await store.dispatch("dmp_panels/syncPanelDevices", panel.value.id);
        await fetchDevices({ sync: false });
        toast({
          component: ToastificationContent,
          props: {
            title: trans["dmp.syncDevicesSuccess"],
            icon: "CheckIcon",
            variant: "success",
          },
        });
      } catch (error) {
        await fetchDevices({ sync: false });
        toast({
          component: ToastificationContent,
          props: {
            title: trans["dmp.syncDevicesError"],
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        loading.value = false;
        loadingToast.value.close();
      }
    }

    async function updateStatus() {
      // loading.value = true;
      // await fetchDevices();
      // loading.value = false;
    }

    const toggleArea = (areaId) => {
      const index = expandedAreas.value.indexOf(areaId);
      if (index > -1) {
        expandedAreas.value.splice(index, 1);
      } else {
        expandedAreas.value.push(areaId);
      }
    };

    const getCategoryKey = (deviceType, id) => `${deviceType}_${id}`;

    const getDeviceTag = (deviceType, id) => {
      return deviceTags.value[getCategoryKey(deviceType, id)] || null;
    };

    const setDeviceTag = (deviceType, id, value) => {
      proxy.$set(deviceTags.value, getCategoryKey(deviceType, id), value || null);
    };

    const findDeviceId = (deviceType, id) => {
      if (!panel.value || !panel.value.devices) return null;
      const devices = panel.value.devices;

      if (deviceType === 'panel') {
        return devices.panel ? devices.panel.device_id : null;
      }
      if (deviceType === 'area') {
        const area = (devices.areas || []).find(a => a.id === id);
        return area ? area.device_id : null;
      }
      if (deviceType === 'zone') {
        for (const area of (devices.areas || [])) {
          const zone = (area.devices || []).find(z => z.id === id);
          if (zone) return zone.device_id;
        }
        return null;
      }
      if (deviceType === 'zone24h') {
        const zone = (devices.zones24h || []).find(z => z.id === id);
        return zone ? zone.device_id : null;
      }
      if (deviceType === 'output') {
        const output = (devices.outputs || []).find(o => o.id === id);
        return output ? output.device_id : null;
      }
      if (deviceType === 'door') {
        const door = (devices.doors || []).find(d => d.id === id);
        return door ? door.device_id : null;
      }
      return null;
    };

    const saveDeviceTag = async (tagId, deviceType, deviceIds) => {
      if (!deviceIds || deviceIds.length === 0) return;

      const payload = {
        id_device_tag: tagId,
        dmp_devices: [],
        dmp_areas: [],
      };

      if (deviceType === 'area') {
        payload.dmp_areas = deviceIds;
      } else {
        payload.dmp_devices = deviceIds;
      }

      try {
        const action = tagId
          ? 'dmp_panels/assignDeviceTag'
          : 'dmp_panels/unassignDeviceTag';
        await store.dispatch(action, payload);
        toast({
          component: ToastificationContent,
          props: {
            title: tagId
              ? proxy.$t('dmp.messages.tagAppliedSuccess')
              : proxy.$t('dmp.messages.tagRemovedSuccess'),
            icon: 'CheckIcon',
            variant: 'success',
          },
        });
        return true;
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: error.response?.data?.message || proxy.$t('dmp.messages.commandExecutedError'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
        return false;
      }
    };

    const onChildDeviceTagChange = async (deviceType, id, value) => {
      setDeviceTag(deviceType, id, value);
      const deviceId = findDeviceId(deviceType, id);
      if (deviceId) {
        await saveDeviceTag(value, deviceType, [deviceId]);
      }
    };

    const arraysEqual = (a, b) => {
      if (!a && !b) return true;
      if (!a || !b) return false;
      if (a.length !== b.length) return false;
      const sortedA = [...a].sort();
      const sortedB = [...b].sort();
      return sortedA.every((val, idx) => val === sortedB[idx]);
    };

    const onGroupDeviceTagChange = async (groupType, children, value) => {
      const hasChildWithDifferentValue = children.some((child) => {
        const current = getDeviceTag(child.device_type, child.id);
        return current && current !== value;
      });

      if (hasChildWithDifferentValue) {
        const result = await Swal.fire({
          title: proxy.$t('dmp.tags'),
          text: proxy.$t('dmp.messages.categoryGroupChangeWarning'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: proxy.$t('actions.accept'),
          cancelButtonText: proxy.$t('actions.cancel'),
          customClass: {
            confirmButton: 'btn btn-primary text-capitalize',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.isConfirmed) {
          setDeviceTag(groupType, 'group', null);
          proxy.$nextTick(() => {
            setDeviceTag(groupType, 'group', value);
          });
          return;
        }
      }

      const deviceIds = [];
      children.forEach((child) => {
        setDeviceTag(child.device_type, child.id, value);
        const deviceId = findDeviceId(child.device_type, child.id);
        if (deviceId) deviceIds.push(deviceId);
      });
      setDeviceTag(groupType, 'group', value);

      if (deviceIds.length > 0) {
        const childDeviceType = children[0]?.device_type || groupType;
        await saveDeviceTag(value, childDeviceType, deviceIds);
      }
    };

    const onAreaDeviceTagChange = async (area, value) => {
      const children = area.devices || [];
      const previousTag = getDeviceTag(area.device_type, area.id);
      let applyToZones = false;

      if (children.length > 0) {
        const result = await Swal.fire({
          title: proxy.$t('dmp.tags'),
          text: proxy.$t(value
            ? 'dmp.messages.areaTagApplyToZones'
            : 'dmp.messages.areaTagRemoveFromZones'),
          icon: 'warning',
          showDenyButton: true,
          showCancelButton: true,
          confirmButtonText: proxy.$t('dmp.messages.areaTagIncludeZones'),
          denyButtonText: proxy.$t('dmp.messages.areaTagOnlyArea'),
          cancelButtonText: proxy.$t('actions.cancel'),
          customClass: {
            confirmButton: 'btn btn-primary text-capitalize',
            denyButton: 'btn btn-outline-primary ml-1',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.isConfirmed && !result.isDenied) {
          setDeviceTag(area.device_type, area.id, null);
          proxy.$nextTick(() => {
            setDeviceTag(area.device_type, area.id, previousTag);
          });
          return;
        }
        applyToZones = result.isConfirmed;
      }

      // Save area tag
      const areaDeviceId = findDeviceId('area', area.id);
      if (!areaDeviceId) return;
      const areaSaved = await saveDeviceTag(value, 'area', [areaDeviceId]);
      if (!areaSaved) return;
      setDeviceTag(area.device_type, area.id, value);
      if (!applyToZones) return;

      // Save child zones tags
      const childDeviceIds = [];
      children.forEach((zone) => {
        const deviceId = findDeviceId(zone.device_type, zone.id);
        if (deviceId) childDeviceIds.push(deviceId);
      });

      if (childDeviceIds.length > 0) {
        const zonesSaved = await saveDeviceTag(value, 'zone', childDeviceIds);
        if (zonesSaved) {
          children.forEach((zone) => {
            setDeviceTag(zone.device_type, zone.id, value);
          });
        }
      }
    };

    // Enruta el cambio de etiqueta desde la tab de resultados según el tipo de fila.
    const onResultTagChange = async (result, value) => {
      if (result.kind === 'area') {
        await onAreaDeviceTagChange(result.device, value);
      } else {
        await onChildDeviceTagChange(
          result.device.device_type,
          result.device.id,
          value
        );
      }
    };

    const getStatusClass = (status, deviceType = null) => {
      if (deviceType === "panel") {
        if (status === "ARMED") {
          return "badge badge-success";
        }
        return "badge badge-warning";
      }

      if (deviceType === "door") {
        const statusClasses = {
          LOCKED: "badge badge-danger",
          UNLOCKED: "badge badge-success",
        };
        return statusClasses[status] || "badge badge-secondary";
      }

      const statusClasses = {
        ARMED: "badge badge-success",
        DISARMED: "badge badge-warning",
        NORMAL: "badge badge-info",
        BYPASSED: "badge badge-secondary",
        OFF: "badge badge-dark",
        ON: "badge badge-success",
        OFFLINE: "badge badge-danger",
      };

      return statusClasses[status] || "badge badge-secondary";
    };

    const getDeviceStatusText = (status) => {
      if (!status) return '';
      const key = `dmp.deviceStatuses.${status}`;
      const translated = proxy.$t(key);
      return translated !== key ? translated : status;
    };

    const getPanelStatusText = (status) => {
      return getDeviceStatusText(status);
    };

    const goBack = () => {
      const routeParts = proxy.$route.path.split("/");
      const path = `${routeParts[1]}/${routeParts[2]}`;
      proxy.$router.push(`/${path}`);
    };

    const liveTime = ref(null)
    let intervalId = null


    const normalizeOffset = (offset) => {
      const sign = offset.trim().startsWith('-') ? '-' : '+';
      const hours = offset.replace('-', '').replace('+', '').padStart(2, '0');
      return `${sign}${hours}:00`;
    }

    const formatDateTime = (value) => {
      if (!value) return '';
      return moment(value).format("DD/MM/YYYY hh:mm:ss A");
    }

    const getZoneFromOffset = (offsetString) => {
      const candidates = moment.tz.names().filter(tz => moment.tz(tz).format('Z') === normalizeOffset(offsetString))
      return candidates.find((candiate) => candiate === moment.tz.guess());
    }

    const syncClock = (dateTimeString) => {
      liveTime.value = moment(dateTimeString);
    }

    const startTicking = () => {
      if (intervalId) return;
      intervalId = setInterval(() => {
        if (liveTime.value) {
          liveTime.value = liveTime.value.clone().add(1, 'second');
        }
      }, 1000);
    }


    onMounted(async () => {
      startTicking();
      if (!window.Echo) return
      const panelId = router.currentRoute.params.id || props.panelId;
      window.Echo.private(`panel-devices-${panelId}`)
        .listen('.device.updated', (event) => {
          handlePanelEvent(event)
        })

      await fetchDevices({ sync: false });
      await fetchDevices({ sync: true });
    })

    onBeforeUnmount(() => {
      clearInterval(checkStatusInterval.value);

      const panelId = router.currentRoute.params.id || props.panelId;
      window.Echo.leave(`panel-devices-${panelId}`)
    })

    function handlePanelEvent(event) {
      if (!panel.value.devices) return

      let metadata
      try {
        metadata = typeof event.metadata === 'string'
          ? JSON.parse(event.metadata)
          : event.metadata
      } catch { return }

      const { device_type, device_id, active } = metadata

      if (active) {
        panel.value.devices.panel.status = 'ARMED'
      } else {
        panel.value.devices.panel.status = 'DISARMED'
      }

      const newStatus = mapEventToStatus(metadata)

      if (!newStatus) return

      const normalizeId = (id) => String(parseInt(id, 10))
      const normalizeDoorId = (id) => {
        const num = parseInt(id, 10)
        return num < 10 ? `D0${num}` : `D${num}`
      }

      if (device_type === 'area') {
        const area = (panel.value.devices.areas || [])
          .find(a => normalizeId(a.id) === normalizeId(device_id))
        if (area) area.status = newStatus
        return
      }

      if (device_type === 'zone' || device_type === 'zone24h') {
        let found = false
        for (const area of (panel.value.devices.areas || [])) {
          const zone = (area.devices || [])
            .find(z => normalizeId(z.id) === normalizeId(device_id))
          if (zone) {
            zone.status = newStatus;
            if (metadata.virtual_bypass !== undefined) {
              zone.virtual_bypass = !!metadata.virtual_bypass
            }
            found = true;
            break
          }
        }
        if (!found) {
          const zone24h = (panel.value.devices.zones24h || [])
            .find(z => normalizeId(z.id) === normalizeId(device_id))
          if (zone24h) {
            zone24h.status = newStatus;
            if (metadata.virtual_bypass !== undefined) {
              zone24h.virtual_bypass = !!metadata.virtual_bypass
            }
          }
        }
        return
      }


      if (device_type === 'door') {
        const door = (panel.value.devices.doors || [])
          .find(d => d.id === normalizeDoorId(device_id))
        if (door) door.status = newStatus
        return
      }

      if (device_type === 'output') {
        const output = (panel.value.devices.outputs || [])
          .find(d => normalizeId(d.id) === normalizeId(device_id))
        if (output) output.status = newStatus
        return
      }
    }

    function applyPanel(data) {
      panel.value = data;
      initializeDeviceTags();
    }


    onUnmounted(() => {
      clearInterval(intervalId);
    });

    watch(
      () => panel.value?.center?.tenant_id,
      (tenantId) => {
        if (tenantId) fetchTagOptions();
      },
      { immediate: true }
    );

    watch(
      () => panel.value?.device?.camera?.summary?.dateTime,
      (newVal) => {
        if (newVal) syncClock(newVal);
      },
      { immediate: true }
    );

    return {
      handleVirtualBypass,
      fields,
      loading,
      panel,
      panelStatuses,
      bTable,
      textFilter,
      areasSearch,
      zones24hSearch,
      outputsSearch,
      doorsSearch,
      activeTabIndex,
      hasGlobalSearch,
      searchResults,
      searchResultsCount,
      onResultTagChange,
      syncDevices,
      loadingToast,
      styleStatusPanel,
      textStatusPanel,
      devicesFiltered,
      panelStatus,
      updateStatus,
      trans,
      isDark,
      goBack,
      expandedAreas,
      toggleArea,
      getStatusClass,
      getPanelStatusText,
      filteredAreas,
      areasWithoutZones,
      areasWithZones,
      filteredZones24h,
      filteredOutputs,
      filteredDoors,
      areasCount,
      zones24hCount,
      outputsCount,
      doorsCount,
      tagOptions,
      deviceTags,
      getDeviceTag,
      setDeviceTag,
      onChildDeviceTagChange,
      onGroupDeviceTagChange,
      onAreaDeviceTagChange,
      getCategoryKey,
      findDeviceId,
      saveDeviceTag,
      getDeviceStatusText,
      withPopper: popperHelper.withPopper,
      getZoneTypeText,
      formatDateTime,
      getZoneFromOffset,
      liveTime,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables/_variables.scss";

.test-scss-variables-container {
  background-color: $accent-custom-color;
  color: $theme-dark-headings-color;
  padding: 1rem;
  border-radius: 0.25rem;
  margin-bottom: 1rem;
}

.devices-table-wrapper {
  overflow-x: unset;

  &--grid {
    min-width: max-content;
  }
}

.table-fields-devices {
  border-top: 1px solid #ebe9f1;
  border-bottom: 1px solid #ebe9f1;
  background-color: #f3f2f7;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  min-width: 100%;

  .dark-layout & {
    background-color: $theme-dark-table-header-bg;
    border-color: var(--third-dark);
  }
}

.table-title {
  color: #6e6b7b;
  font-size: 0.857rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 700;
  text-transform: uppercase;

  .dark-layout & {
    color: $theme-dark-headings-color;
  }
}

.device-row {
  border: 1px solid #ebe9f1;
  margin-bottom: 1px;
  min-width: 100%;

  .dark-layout & {
    background-color: var(--second-dark);
    color: $theme-dark-label-color;
  }
}

.device-row:hover {
  background-color: #f8f9fa;

  .dark-layout & {
    background-color: darken($second-dark, 5%);
  }
}

.area-header {
  background-color: #f3f2f7;
  border: 1px solid #d8d6de;
  transition: all 0.3s ease;

  .dark-layout & {
    background-color: $theme-dark-table-row-bg;
    border-color: var(--third-dark);
  }
}

.area-header:hover {
  background-color: #e7e7e7;
  border-color: #c2bfc7;

  .dark-layout & {
    background-color: $theme-dark-table-hover-bg;
    border-color: var(--third-dark);
  }
}

.area-expanded {
  border-bottom-color: #6e6b7b !important;
  background-color: #e7e7e7;
}

.area-accordion {
  border-radius: 4px;
  overflow: visible;
  min-width: 100%;
}

// Separador entre el grupo de áreas sin zonas y el grupo con zonas
.areas-group-divider {
  min-width: 100%;
  height: 1px;
  margin: 0.75rem 0;
  background-color: transparent;

  .dark-layout & {
    background-color: var(--third-dark);
  }
}

// Áreas sin zonas: sin cursor de acordeón (no son desplegables)
.area-header--no-zones {
  cursor: default;
}

.area-content {
  background-color: #fafafa;
}

.zones-container {
  border-left: 3px solid #d8d6de;
  padding-left: 1rem;
}

.zone-row {
  background-color: #fff;
  margin-left: 0;
}

.zone-row:last-child {
  border-bottom: none;
}

.transition-all {
  transition: all 0.2s ease;
}

.cursor-pointer {
  cursor: pointer;
}

.badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
}

.device-table-grid {
  display: grid !important;
  grid-template-columns: 1.2fr 1.5fr .9fr 1fr 1.1fr 1fr 1.8fr;
  align-items: center;
  width: 100%;
}

.device-table-grid>.col,
.device-table-grid>[class*="col-"] {
  max-width: none !important;
  flex: unset !important;
  width: auto !important;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

// Tabs por tipo de device
.dmp-device-tabs {
  ::v-deep .nav-pills {
    margin-bottom: 0.5rem;
    gap: 0.25rem;
  }

  ::v-deep .nav-pills .nav-link {
    display: inline-flex;
    align-items: center;
    font-weight: 600;
    color: #6e6b7b;
    border-radius: 0.358rem;
    padding: 0.5rem 1rem;
  }

  ::v-deep .nav-pills .nav-link.disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .dark-layout & {
    ::v-deep .nav-pills .nav-link {
      color: $theme-dark-label-color;
    }
  }
}

// Barra para asignar una etiqueta a todo el grupo de la tab
.group-tag-bar {
  background-color: #fbfbfc;

  .dark-layout & {
    background-color: $theme-dark-table-row-bg;
  }
}

// Buscador propio de cada tab
.tab-search {
  display: flex;
  justify-content: flex-end;
}

.tab-search-input {
  max-width: 320px;
}

/* ============================================================
   Navegación: volver
   ============================================================ */
.dmp-goback {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.75rem 0 0.75rem 0.5rem;
  cursor: pointer;
  user-select: none;
}

.dmp-goback__text {
  font-weight: 600;
  color: #6e6b7b;
  transition: color 0.2s ease;

  .dark-layout & {
    color: $theme-dark-label-color;
  }
}

.dmp-goback:hover .dmp-goback__text {
  color: var(--primary);
}

/* ============================================================
   Encabezado del panel (título + búsqueda + sincronizar)
   ============================================================ */
.dmp-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;

  &--compact {
    margin-bottom: 1rem;
  }
}

.dmp-header__identity {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.dmp-header__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.65rem;
  background: rgba(var(--primary-rgb, 27, 61, 94), 0.12);
  color: var(--primary);
  font-size: 1.35rem;

  .dark-layout & {
    background: rgba(255, 255, 255, 0.1);
    color: $theme-dark-headings-color;
  }
}

.dmp-header__text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.dmp-header__name {
  margin: 0;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .dark-layout & {
    color: $theme-dark-headings-color;
  }
}

.dmp-header__status {
  align-self: flex-start;
}

.dmp-header__tools {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-left: auto;
}

.dmp-search {
  position: relative;
  display: flex;
  align-items: center;
}

.dmp-search__icon {
  position: absolute;
  left: 0.75rem;
  color: #b9b9c3;
  font-size: 1rem;
  pointer-events: none;
}

.dmp-search__input {
  padding-left: 2.25rem;
  min-width: 220px;
}

.dmp-header__sync {
  display: inline-flex;
  align-items: center;
}

.dmp-spin {
  animation: dmp-spin 0.9s linear infinite;
}

@keyframes dmp-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ============================================================
   Panel principal (hero)
   ============================================================ */
.panel-hero {
  margin-bottom: 1.5rem;
  border: 1px solid #ebe9f1;
  border-radius: 0.6rem;
  background: #fff;
  box-shadow: 0 2px 8px rgba(34, 41, 47, 0.06);
  overflow: hidden;

  &--compact {
    margin-bottom: 1rem;
    border-radius: 0.45rem;
  }

  .dark-layout & {
    background: var(--second-dark);
    border-color: var(--third-dark);
  }
}

.panel-hero__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #ebe9f1;
  background: linear-gradient(90deg, rgba(var(--primary-rgb, 27, 61, 94), 0.07), rgba(var(--primary-rgb, 27, 61, 94), 0));

  .dark-layout & {
    border-color: var(--third-dark);
    background: linear-gradient(90deg, rgba(var(--primary-rgb, 27, 61, 94), 0.18), rgba(var(--primary-rgb, 27, 61, 94), 0));
  }
}

.panel-hero__identity {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  min-width: 0;
}

.panel-hero__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 3rem;
  height: 3rem;
  border-radius: 0.7rem;
  background: var(--primary);
  color: #fff;
  font-size: 1.5rem;
  box-shadow: 0 4px 12px rgba(var(--primary-rgb, 27, 61, 94), 0.35);
}

.panel-hero__titles {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
}

.panel-hero__name {
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.2;
  color: #5e5873;

  .dark-layout & {
    color: $theme-dark-headings-color;
  }
}

.panel-hero__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.panel-hero__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.15rem 0.6rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6e6b7b;
  background: #f3f2f7;

  i {
    font-size: 0.85rem;
  }

  .dark-layout & {
    color: $theme-dark-label-color;
    background: var(--third-dark);
  }
}

.panel-hero__state {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.85rem;
  font-size: 0.8rem;
  font-weight: 600;
}

.panel-hero__state-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: currentColor;
  animation: dmp-pulse 1.8s ease-out infinite;
}

@keyframes dmp-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.5);
  }

  100% {
    box-shadow: 0 0 0 6px rgba(255, 255, 255, 0);
  }
}

.panel-hero__body {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  padding: 1.25rem;

  @media (min-width: 992px) {
    grid-template-columns: 2fr 1fr;
  }
}

.panel-hero__section-title {
  display: block;
  margin-bottom: 0.75rem;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #b9b9c3;
}

.panel-hero__section--actions {
  padding-top: 1.25rem;
  border-top: 1px solid #ebe9f1;

  @media (min-width: 992px) {
    padding-top: 0;
    padding-left: 1.25rem;
    border-top: none;
    border-left: 1px solid #ebe9f1;
  }

  .dark-layout & {
    border-color: var(--third-dark);
  }
}

.panel-stats {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.6rem;
}

.panel-stat {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid #ebe9f1;
  border-radius: 0.5rem;
  background: #fafafc;

  >i {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 2rem;
    height: 2rem;
    border-radius: 0.45rem;
    background: rgba(var(--primary-rgb, 27, 61, 94), 0.1);
    color: var(--primary);
    font-size: 1rem;

    .dark-layout & {
      background: rgba(255, 255, 255, 0.12);
      color: $theme-dark-headings-color;
    }
  }

  .dark-layout & {
    background: var(--third-dark);
    border-color: var(--third-dark);
  }
}

.panel-stat__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.panel-stat__label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #b9b9c3;
}

.panel-stat__value {
  font-size: 0.9rem;
  font-weight: 600;
  color: #5e5873;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .dark-layout & {
    color: $theme-dark-headings-color;
  }
}

.panel-hero__actions {
  ::v-deep .dmp-panel-action {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    width: 100%;
    margin-bottom: 0.5rem;
    padding: 0.5rem 0.85rem;
    border: 1px solid rgba(var(--primary-rgb, 27, 61, 94), 0.4);
    border-radius: 0.4rem;
    color: var(--primary);
    font-size: 0.85rem;
    font-weight: 600;
    transition: all 0.2s ease;

    &:last-child {
      margin-bottom: 0;
    }

    &:hover {
      background: var(--primary);
      color: #fff;
    }

    .dark-layout & {
      color: $theme-dark-headings-color;
      border-color: var(--third-dark);

      &:hover {
        background: var(--primary);
        color: #fff;
      }
    }
  }
}
</style>
